import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Search, Megaphone, MousePointerClick, LayoutGrid, Users, Layers } from "lucide-react";
import MarqueeBand from "@/components/MarqueeBand";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { openBooking } from "@/lib/api";

const LIME_ART =
  "https://images.unsplash.com/photo-1764303017772-2935b020e23b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGRpZ2l0YWwlMjBhcnQlMjBsaW1lJTIwZ3JlZW58ZW58MHx8fHwxNzg3MjY4NDgyfDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: Search,
    title: "Search Engine Optimization",
    tag: "SEO",
    text: "Page one isn't luck — it's engineering. We crawl your site, your competitors, and your market, then build the trail Google can't ignore.",
    points: ["Technical & local SEO audits", "Keyword & content strategy", "Authority building & digital PR", "Google Business Profile domination"],
  },
  {
    icon: Megaphone,
    title: "Social Media Marketing",
    tag: "SOCIAL",
    text: "Scroll-stopping content with a strategy behind it. We turn followers into a colony that buys, shares, and recruits.",
    points: ["Channel strategy & calendars", "Content production & design", "Community management", "Influencer & UGC programs"],
  },
  {
    icon: MousePointerClick,
    title: "Paid Advertising",
    tag: "PPC / PAID",
    text: "Every dollar accounted for. We build, launch, and relentlessly optimize campaigns across Google, Meta, and beyond.",
    points: ["Google & Meta Ads management", "Landing page CRO", "Retargeting funnels", "Weekly performance reporting"],
  },
  {
    icon: LayoutGrid,
    title: "Web Design & Development",
    tag: "WEB",
    text: "Your site is the colony's nest. We design fast, brutal, conversion-obsessed websites that turn traffic into revenue.",
    points: ["Conversion-first UX/UI design", "Blazing-fast development", "E-commerce & booking builds", "Hosting, care & iteration"],
  },
  {
    icon: Users,
    title: "Lead Generation",
    tag: "LEAD GEN",
    text: "Pipelines that never sleep. We engineer the funnels, forms, and follow-ups that keep your sales team fed.",
    points: ["Funnel & offer strategy", "High-converting landing pages", "Email & SMS nurture flows", "CRM & automation setup"],
  },
  {
    icon: Layers,
    title: "And Everything Around It",
    tag: "FULL STACK",
    text: "Content, email marketing, branding, analytics — the connective tissue that makes every channel hit harder.",
    points: ["Content marketing & copywriting", "Email marketing & automation", "Brand identity & messaging", "Analytics & attribution"],
  },
];

const ServiceSection = ({ s, index }) => (
  <FadeUp className="border-b-2 border-black" delay={0.05}>
    <div
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-5 md:px-10 py-16 md:py-20 group hover:bg-[#F4F4F4] transition-colors duration-300"
      data-testid={`service-section-${s.tag.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4">
        <span className="font-display text-5xl md:text-6xl text-black/15 group-hover:text-lime group-hover:[-webkit-text-stroke:2px_#0A0A0A] transition-colors duration-300">
          {String(index + 1).padStart(2, "0")}
        </span>
        <s.icon size={34} strokeWidth={2.2} />
      </div>
      <div className="lg:col-span-5">
        <p className="kicker mb-3">{s.tag}</p>
        <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.9]">{s.title}</h2>
        <p className="mt-5 font-medium text-base md:text-lg leading-relaxed max-w-lg">{s.text}</p>
      </div>
      <div className="lg:col-span-5">
        <ul className="space-y-3">
          {s.points.map((p) => (
            <li key={p} className="flex items-center gap-3 font-mono text-sm uppercase tracking-widest border-b border-black/10 pb-3">
              <span className="w-2.5 h-2.5 bg-lime border-2 border-black shrink-0" />
              {p}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={openBooking} data-testid={`service-book-${index}`} className="btn-brutal text-xs">
            Talk to Us <ArrowUpRight size={14} />
          </button>
          <Link to="/contact" data-testid={`service-quote-${index}`} className="btn-brutal-white text-xs">
            Get a Quote
          </Link>
        </div>
      </div>
    </div>
  </FadeUp>
);

export default function Services() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <>
      <section ref={heroRef} className="relative overflow-hidden border-b-2 border-black" data-testid="services-hero">
        <motion.div style={{ y: yImg }} className="absolute right-0 top-0 w-[55vw] md:w-[32vw] opacity-25 pointer-events-none select-none">
          <img src={LIME_ART} alt="" className="w-full" />
        </motion.div>
        <div className="relative px-5 md:px-10 pt-16 md:pt-24 pb-16">
          <p className="kicker mb-6" data-testid="services-kicker">
            <span className="bg-black text-lime px-2 py-1">Services</span>
          </p>
          <h1 className="font-display uppercase text-[13vw] md:text-[9vw] leading-[0.85]" data-testid="services-heading">
            <MaskedLine delay={0.1}>Every trail</MaskedLine>
            <MaskedLine delay={0.25}>
              leads to <span className="bg-lime px-2 box-decoration-clone">growth.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-8 max-w-xl font-medium text-base md:text-lg leading-relaxed"
            data-testid="services-subtext"
          >
            Six disciplines, one colony. Pick a single trail or hand us the whole map — every
            engagement starts with strategy and ends in numbers you can bank on.
          </motion.p>
        </div>
      </section>

      <MarqueeBand dark items={["Search", "Social", "Paid", "Web", "Leads", "Content", "Email", "Brand"]} />

      <div data-testid="services-list">
        {services.map((s, i) => (
          <ServiceSection key={s.tag} s={s} index={i} />
        ))}
      </div>

      <section className="px-5 md:px-10 py-20 md:py-28 bg-lime border-b-2 border-black" data-testid="services-cta">
        <FadeUp>
          <h2 className="font-display text-[10vw] md:text-[6vw] uppercase leading-[0.9]">
            Not sure which trail? Get the map free.
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="services-audit-button" className="btn-brutal">
              Request a Free Audit <ArrowUpRight size={16} />
            </Link>
            <button onClick={openBooking} data-testid="services-book-button" className="btn-brutal-white">
              Book a Consultation
            </button>
          </div>
        </FadeUp>
      </section>
    </>
  );
}
